import {Component} from 'react';
import PropTypes from 'prop-types';
import { Container, Paper, Tooltip, styled } from '@mui/material';
import editIcon from 'images/editIcon.svg';
import 'components/EditableText.scss';

// Note: editStyle properties will be applied with !important to override semantic-ui styles
const StyledTextarea = styled('textarea', {
  shouldForwardProp: (prop) => prop !== 'width' && prop !== 'editStyle' && prop !== 'resizable',
})(({ editStyle, resizable, width }) => {
  const importantEditStyle = Object.fromEntries(
      Object.entries(editStyle || {}).map(([key, value]) => [key, `${value} !important`])
  );
  return {
    ...importantEditStyle,
    resize: resizable ? 'vertical !important' : 'none !important',
    width: width ? `${width} !important` : '100% !important',
    padding: '4px !important',
    lineHeight: '1.39',
    '&:focus': {
      outline: 'none',
      borderColor: '#3399ff',
    },
  };
});

export default class EditableText extends Component {
  static propTypes = {
    className: PropTypes.string,
    dataTestId: PropTypes.string,
    editClassName: PropTypes.string,
    editIsEnabled: PropTypes.bool,
    editMaxLength: PropTypes.number,
    editStyle: PropTypes.object,
    width: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    editRows: PropTypes.number,
    onUpdateData: PropTypes.func,
    onChangeValidator: PropTypes.func,
    placeholder: PropTypes.string,
    popupProps: PropTypes.shape({
      header: PropTypes.string,
      content: PropTypes.node.isRequired,
    }),
    rawText: PropTypes.string,
    renderedText: PropTypes.node,
    renderedTextRef: PropTypes.any,
    resizable: PropTypes.bool,
  };

  static defaultProps = {
    rawText: '',
    className: '',
    editIsEnabled: false,
    editMaxLength: 8,
    editRows: 1,
    onUpdateData: () => {
    },
    onChangeValidator: () => true,
    resizable: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      editing: false,
      rawText: props.rawText,
      anchorEl: null,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.rawText !== prevProps.rawText || this.props.editIsEnabled !== prevProps.editIsEnabled) {
      this.setState({
        rawText: this.props.rawText,
        editing: false,
      });
    }
  }

  toggleEditMode = () => {
    this.props.editIsEnabled && this.setState({editing: !this.state.editing});
  };

  setAnchorEl = (element) => {
    this.setState({anchorEl: element});
  };

  onEditFocus = (event) => {
    this.resizeText(event);
    event.target.setSelectionRange(0, event.target.value.length);
    event.preventDefault();
  };

  onBlurFocus = () => {
    this.setAnchorEl(null);
    const rawText = this.state.rawText;

    this.props.onUpdateData({rawText});

    this.setState(() => ({
      editing: false,
      rawText,
    }));
  };

  onChangeTextInput = (event) => {
    this.resizeText(event);
    const value = event.target.value;
    const {onChangeValidator} = this.props;
    if (onChangeValidator(value)) {
      this.setState(() => ({
        rawText: value,
      }));
    }
    event.preventDefault();
  };

  resizeText = (event) => {
    const text = event.target;
    text.style.height = '';
    text.style.height = text.scrollHeight + 3 + 'px';
  };

  renderTextView = () => {
    const {editIsEnabled, placeholder, rawText, renderedText, renderedTextRef} = this.props;
    return (
      <div className={editIsEnabled ? 'view field' : 'view'} ref={renderedTextRef} onClick={this.toggleEditMode}>
        {
          renderedText || rawText
        }
        {
          !renderedText && !rawText && placeholder
          && <span className="placeholder">{placeholder}</span>
        }
        {
          editIsEnabled && !renderedText && <img alt="edit-icon" src={editIcon}/>
        }
      </div>
    );
  };

  renderEditView = () => {
    const {rawText} = this.state;
    const {
      editClassName,
      editMaxLength,
      editStyle,
      width,
      editRows,
      placeholder,
      popupProps,
      resizable
    } = this.props;
    const classNames = ['edit', editClassName].join(' ').trim();
    const input = (
      <div className={classNames}>
        <StyledTextarea autoFocus
                  name="text"
                  placeholder={placeholder}
                  maxLength={editMaxLength}
                  ref={this.setAnchorEl}
                  rows={editRows}
                  value={rawText}
                  onChange={this.onChangeTextInput}
                  onFocus={this.onEditFocus}
                  onBlur={this.onBlurFocus}
                  onKeyPress={(event) => (event.key === 'Enter') && this.onBlurFocus()}
                  editStyle={editStyle}
                  width={width}
                  resizable={resizable}
        />
      </div>
    );

    if (popupProps) {
      return (
        <Tooltip
          id="editable-text-tooltip"
          open={true}
          placement="top"
          arrow
          title={
            <Paper elevation={0}>
              <div className="header">
                {popupProps.header}
              </div>
              {popupProps.content}
            </Paper>
          }>
          {input}
        </Tooltip>
      );
    }
    else {
      return input;
    }
  };

  render() {
    const {editing} = this.state;
    const {className, dataTestId} = this.props;

    return (
      <Container
          disableGutters
          className={className}
          data-testid={dataTestId}
          maxWidth={false}
          sx={{
            margin: 0,
            lineHeight: 1,
          }}
      >
        {
          editing
            ? this.renderEditView()
            : this.renderTextView()
        }
      </Container>
    );
  }
}
